import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CardImage } from "@/components/CardImage";
import { OFFICIAL_CARDS } from "@/lib/tcg-cards";
import { RARITY_MEANINGS } from "@/lib/tcg-cards";

/**
 * Public share page for a single pulled card. No account, no sign-in wall: this
 * is the URL that goes out to X, TikTok, Instagram, Discord and forums, so the
 * card art, rarity and grade are all readable to anonymous visitors. An account
 * is only needed to claim the 5 free daily packs.
 */
export const Route = createFileRoute("/pull/$code")({
  loader: ({ params }) => {
    const card = OFFICIAL_CARDS.find((c) => c.code === params.code);
    if (!card) throw notFound();
    return { card };
  },
  head: ({ loaderData, params }) => {
    const card = loaderData?.card;
    const name = card ? `${card.name} (${card.code})` : params.code;
    const url = `https://palstars.com/pull/${params.code}`;
    return {
      meta: [
        { title: `${name} — Palworld Card Pull | Pal Stars` },
        { name: "description", content: `${name} pulled from a Palworld Official Card Game booster pack on Pal Stars. View the official card scan, rarity and grading report.` },
        { property: "og:title", content: `${name} — Palworld card pull` },
        { property: "og:description", content: "Pulled from a Palworld booster pack on Pal Stars. Open your own free packs — no account needed to look." },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(card?.image
          ? [
              { property: "og:image", content: card.image },
              { name: "twitter:image", content: card.image },
            ]
          : []),
        { name: "robots", content: "noindex,follow" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PullSharePage,
});

function PullSharePage() {
  const { card } = Route.useLoaderData();
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const grade = search.get("grade");
  const by = search.get("by");

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <p className="text-xs uppercase tracking-widest text-primary">Palworld OFFICIAL CARD GAME</p>
      <h1 className="mt-2 text-2xl sm:text-3xl font-bold">
        {card.name} <span className="font-mono text-base text-muted-foreground">{card.code}</span>
      </h1>
      <p className="aeo-answer mt-3 text-sm text-muted-foreground">
        {by ? `${by} pulled ` : "This is "}{card.name} ({card.rarity}
        {grade ? `, graded ${grade}` : ""}) from a Palworld booster pack on Pal Stars. Anyone can view this card —
        you only need a free account if you want your own 5 free packs every day.
      </p>

      <div className="mt-6 grid gap-6 sm:grid-cols-[260px_1fr] items-start">
        <CardImage
          src={card.image}
          alt={`${card.name} ${card.code} ${card.rarity} Palworld card`}
          width={260}
          height={363}
          priority
          responsive
          className="w-full max-w-[260px] rounded-xl border"
        />
        <div className="space-y-2 text-sm">
          <p><strong>Rarity:</strong> {card.rarity} — {RARITY_MEANINGS[card.rarity as keyof typeof RARITY_MEANINGS]}</p>
          <p><strong>Type:</strong> {card.type} · <strong>Color:</strong> {card.color}</p>
          {card.power !== null && <p><strong>Power:</strong> {card.power} · <strong>Cost:</strong> {card.cost ?? "—"}</p>}
          {grade && <p><strong>Grade:</strong> {grade} / 10</p>}
          <div className="pt-3 flex flex-wrap gap-2">
            <Link to="/pack-opening" className="rounded-md bg-primary px-4 py-2 font-semibold text-primary-foreground hover:brightness-110">
              Open a free pack
            </Link>
            <Link to="/cards" className="rounded-md border px-4 py-2 font-semibold hover:bg-card">Card database</Link>
            <Link to="/guides/$slug" params={{ slug: "palstars-palworld-card-game" }} className="rounded-md border px-4 py-2 font-semibold hover:bg-card">
              Card game guide
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
